import * as React from "react";
import {
  FloatingFocusManager,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
} from "@floating-ui/react";
import { useIcon } from "@salt-ds/core";

import { Button } from "../../controls/Button";
import { usePortalScopeClassName } from "../../floating/usePortalScope";
import { isHTMLElement, seedWidthMap, updateWidthMap } from "./measurement";
import { TabSlot } from "./TabSlot";
import { useTabs } from "./context";
import "./tab-overflow-list.css";

/**
 * The "…" button and the menu of tabs that did not fit.
 *
 * ── THE BUTTON IS A TAB, WITH role="tab" AND tabIndex -1 ──────────────────────────────────
 *
 * It joins the same navigation collection as the real tabs, so ArrowRight from the last visible
 * tab reaches it rather than falling off the end of the strip. It is never a tab STOP of its own
 * (`tabIndex={-1}`) because the whole tablist is one tab stop — Tab moves past the bar, arrows
 * move within it.
 *
 * ── THE MENU CLOSES WHEN ANYTHING RESIZES ─────────────────────────────────────────────────
 *
 * A resize changes which tabs belong in the menu, and re-sorting a list the user is currently
 * reading moves the item under their pointer. Closing is blunt and honest: the menu reopens with
 * the correct contents.
 */
export interface TabOverflowListProps extends React.HTMLAttributes<HTMLElement> {
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  hiddenValues: string[];
  open: boolean;
  order: number;
  setOpen: (open: boolean) => void;
}

export const TabOverflowList = React.forwardRef<HTMLDivElement, TabOverflowListProps>(
  function TabOverflowList({ buttonRef, className, hiddenValues, open, order, setOpen, ...rest }, ref) {
    const { OverflowIcon } = useIcon();
    const { activeTab, registerTab, updateTab } = useTabs();
    const overflowRef = React.useRef<HTMLButtonElement | null>(null);
    const hadOverflowItemsRef = React.useRef(false);
    const overflowId = React.useId();
    const overlayId = React.useId();
    const [referenceEl, setReferenceEl] = React.useState<Element | null>(null);
    const portalScope = usePortalScopeClassName(referenceEl);

    const { context, floatingStyles, refs } = useFloating({
      open,
      onOpenChange: (nextOpen, _event, reason) => {
        setOpen(nextOpen);
        // Escape returns focus to the button that opened the menu. Without this focus falls to
        // the body and a keyboard user loses their place in the tab bar entirely.
        if (reason === "escape-key") overflowRef.current?.focus();
      },
      placement: "bottom-start",
      middleware: [
        offset(1),
        size({
          apply({ elements, availableHeight }: { elements: { floating: HTMLElement }; availableHeight: number }) {
            // Floored at five rows: a menu clamped to two near the bottom of the window is
            // unusable, and scrolling the page to fix it closes the menu.
            Object.assign(elements.floating.style, {
              maxHeight: `max(calc((var(--salt-size-base) + var(--salt-spacing-100)) * 5), calc(${availableHeight}px - var(--salt-spacing-100)))`,
            });
          },
        }),
        flip(),
        shift({ padding: 8 }),
      ],
    });

    const { getFloatingProps, getReferenceProps } = useInteractions([
      useClick(context),
      useDismiss(context),
    ]);

    const handleButtonRef = useMergeRefs([
      buttonRef,
      overflowRef,
      refs.setReference,
      (node: HTMLButtonElement | null) => setReferenceEl(node),
    ]);
    const handleListRef = useMergeRefs([ref, refs.setFloating]);

    const hasOverflowItems = hiddenValues.length > 0;
    const initialOrderRef = React.useRef(order);

    React.useEffect(() => {
      const tabList = overflowRef.current?.parentElement;
      if (!open || !tabList || typeof ResizeObserver === "undefined") return;
      const observed = [tabList, tabList.parentElement].filter(
        (element): element is HTMLElement => element != null,
      );
      const widths = seedWidthMap(observed);
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (!isHTMLElement(entry.target)) continue;
          const nextWidth = entry.contentRect.width || entry.target.getBoundingClientRect().width;
          if (updateWidthMap(widths, entry.target, nextWidth)) {
            setOpen(false);
            return;
          }
        }
      });
      for (const element of observed) observer.observe(element);
      return () => observer.disconnect();
    }, [open, setOpen]);

    React.useLayoutEffect(() => {
      // An open menu with nothing in it is a menu the user cannot escape by choosing something.
      if (open && !hasOverflowItems) setOpen(false);
    }, [hasOverflowItems, open, setOpen]);

    React.useLayoutEffect(() => {
      // Overflow appearing means the layout just changed underneath an open menu.
      if (hasOverflowItems && !hadOverflowItemsRef.current) setOpen(false);
      hadOverflowItemsRef.current = hasOverflowItems;
    }, [hasOverflowItems, setOpen]);

    React.useLayoutEffect(() => {
      if (!overflowRef.current || !hasOverflowItems) return;
      return registerTab({
        element: overflowRef.current,
        id: overflowId,
        location: "main",
        order: initialOrderRef.current,
        value: overflowId,
      });
    }, [hasOverflowItems, overflowId, registerTab]);

    React.useLayoutEffect(() => {
      if (!hasOverflowItems) return;
      // Ordered AFTER every real tab, so arrow navigation reaches it last rather than in the
      // middle of the strip.
      updateTab(overflowId, { element: overflowRef.current, location: "main", order });
    }, [hasOverflowItems, order, overflowId, updateTab]);

    if (!hasOverflowItems) return null;

    return (
      <>
        <Button
          appearance="transparent"
          aria-controls={overlayId}
          aria-expanded={open}
          aria-label="Overflow"
          className={["saltTabOverflowList", className].filter(Boolean).join(" ")}
          data-overflowbutton
          role="tab"
          sentiment="neutral"
          tabIndex={-1}
          {...getReferenceProps({
            onFocus: () => {
              activeTab.current = { id: overflowId, value: overflowId };
            },
          })}
          ref={handleButtonRef}
          {...rest}
        >
          <OverflowIcon aria-hidden />
        </Button>
        {open ? (
          <div className={portalScope}>
            {/*
              Focus moves INTO the menu on open. Without it a keyboard user opens the menu and
              their arrow keys go nowhere, because focus is still on the button behind it — the
              menu is visible and unreachable. MEASURED against the vendor: it puts focus inside,
              this port did not until this was added.

              `modal: false` — the rest of the page stays reachable, matching a menu rather than
              a dialog. `returnFocus: false` because choosing a tab moves focus to that tab in
              the strip; returning it to the button would immediately undo that.
              `closeOnFocusOut` so tabbing past the last item closes the menu instead of leaving
              it open behind the user.
            */}
            <FloatingFocusManager
              closeOnFocusOut
              context={context}
              initialFocus={0}
              modal={false}
              returnFocus={false}
            >
            <div
              className="saltTabOverflowList-list"
              ref={handleListRef}
              style={floatingStyles}
              {...getFloatingProps({
                id: overlayId,
                onKeyDown: (event: React.KeyboardEvent) => {
                  // Shift-Tab out of the menu goes back to the button, not to whatever precedes
                  // the portal in the DOM — the portal is elsewhere in the document entirely.
                  if (event.key !== "Tab" || !event.shiftKey) return;
                  event.preventDefault();
                  setOpen(false);
                  requestAnimationFrame(() =>
                    overflowRef.current?.focus({ preventScroll: true }),
                  );
                },
              })}
            >
              {/*
                A SECOND tablist, vertical. The menu is a list of tabs in its own right, and
                declaring it horizontal — or not declaring it — would tell a screen reader the
                wrong arrow keys move within it.
              */}
              <div
                aria-label="Overflow tab options"
                aria-orientation="vertical"
                className="saltTabOverflowList-listContainer"
                role="tablist"
              >
                {hiddenValues.map((value) => (
                  <TabSlot key={value} slotId={`overflow:${value}`} value={value} />
                ))}
              </div>
            </div>
            </FloatingFocusManager>
          </div>
        ) : null}
      </>
    );
  },
);
